/**
 * @fileoverview "Foco do Dia" — painel lateral com a triagem do dia para o
 * time comercial. Agrega 3 buckets a partir do estado global de deals:
 *   1) Leads atrasados — lastStatusDate ≥ 14 dias (esfriando)
 *   2) Sem contato há 7+ dias — nenhum contactLog recente
 *   3) Leads quentes parados — score ≥ 80 mas sem update há 3+ dias
 *
 * Aberto via botão na AppTopBar ou via atalho global "f". Click em qualquer
 * item navega para /pipeline e dispara um evento que o Pipeline pode usar
 * para focar o card. Sem dependência de network — tudo do store.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Sun, Flame, Clock, MessageSquare, AlertTriangle, ArrowRight, Phone, MessageCircle, Mail, Star } from 'lucide-react';
import { useStore } from '@/store/useStore';
import type { Deal } from '@/store/useStore';
import { getInitials, cn, avatarFallbackStyle } from '@/lib/utils';
import { useLeadMutations } from '@/modules/crm/hooks/useLeadMutations';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useWatchlist } from '@/hooks/useWatchlist';
import { useDealsList } from '@/hooks/useDealsList';
import { useStageSemantics } from '@/hooks/useStageSemantics';
import { lastContactDate } from '@/lib/dealEvents';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const HOT_THRESHOLD = 80;
const OVERDUE_DAYS = 14;
const NO_CONTACT_DAYS = 7;
const STALE_HOT_DAYS = 3;

interface BucketItem {
  deal: Deal;
  daysAgo: number;
}

function daysSince(iso?: string | null): number {
  if (!iso) return Infinity;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || Number.isNaN(ms)) return Infinity;
  return ms / (1000 * 60 * 60 * 24);
}

function lastContactDays(d: Deal): number {
  const last = lastContactDate(d);
  return last ? daysSince(last) : Infinity;
}

export default function FocoDoDia() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { deals } = useDealsList();
  const activeProjectType = useStore((s) => s.activeProjectType);
  const activeProjectId = useStore((s) => s.activeProjectId);
  const watchlist = useWatchlist(activeProjectId);
  const stages = useStageSemantics();
  const { saveContactLog } = useLeadMutations();
  const { user } = useCurrentUser();
  const [currentUserName, setCurrentUserName] = useState('Usuário');
  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('name').eq('id', user.id).maybeSingle().then(({ data }) => {
      if (data?.name) setCurrentUserName(data.name);
    });
  }, [user?.id]);

  // Quick contact logger — opens the relevant external intent (tel:,
  // mailto:, wa.me) AND appends a contactLog entry so the timeline
  // reflects the action. Lets the user mark a row "touched" without
  // opening the full lead dialog. Type maps to the icon clicked.
  const handleQuickLog = async (
    deal: Deal,
    type: 'call' | 'whatsapp' | 'email' | 'note',
  ) => {
    // Open external intent (best-effort — some platforms may block).
    if (type === 'call' && deal.phone) window.open(`tel:${deal.phone}`);
    else if (type === 'whatsapp' && deal.phone) window.open(`https://wa.me/${deal.phone.replace(/\D/g, '')}`);
    else if (type === 'email' && deal.email) window.open(`mailto:${deal.email}`);
    const now = new Date();
    const labelByType: Record<typeof type, string> = {
      call: 'Ligação registrada via Foco do dia',
      whatsapp: 'WhatsApp registrado via Foco do dia',
      email: 'Email registrado via Foco do dia',
      note: 'Anotação rápida via Foco do dia',
    };
    await saveContactLog(deal.id, {
      id: crypto.randomUUID(),
      date: now.toISOString().split('T')[0],
      time: now.toTimeString().slice(0, 5),
      user: currentUserName,
      type,
      description: labelByType[type],
    });
    toast.success(`Contato registrado em "${deal.company}"`);
  };

  // Só leads em jogo: ganho/perdido/desqualificado não precisam de "foco" —
  // sem isso o badge marcava 99+ com o cemitério inteiro do funil.
  const buckets = useMemo(() => {
    const overdue: BucketItem[] = [];
    const noContact: BucketItem[] = [];
    const staleHot: BucketItem[] = [];
    const watched: BucketItem[] = [];
    for (const d of deals) {
      if (stages.isClosed(d.column)) continue;
      const updatedDays = daysSince(d.lastStatusDate);
      const contactDays = lastContactDays(d);
      if (updatedDays >= OVERDUE_DAYS) {
        overdue.push({ deal: d, daysAgo: Math.floor(updatedDays) });
      }
      if (contactDays >= NO_CONTACT_DAYS) {
        noContact.push({ deal: d, daysAgo: Number.isFinite(contactDays) ? Math.floor(contactDays) : -1 });
      }
      if (d.score >= HOT_THRESHOLD && updatedDays >= STALE_HOT_DAYS) {
        staleHot.push({ deal: d, daysAgo: Math.floor(updatedDays) });
      }
      if (watchlist.isWatched(d.id)) {
        watched.push({ deal: d, daysAgo: Math.floor(updatedDays === Infinity ? -1 : updatedDays) });
      }
    }
    // Cap each list to a sane size — 12 rows per bucket keeps the panel scannable.
    overdue.sort((a, b) => b.daysAgo - a.daysAgo);
    noContact.sort((a, b) => b.daysAgo - a.daysAgo);
    staleHot.sort((a, b) => b.deal.score - a.deal.score);
    // Watched: surface the most-stale ones first so attention goes where
    // it's needed; leads updated today fall to the bottom.
    watched.sort((a, b) => b.daysAgo - a.daysAgo);
    return {
      overdue: overdue.slice(0, 12),
      overdueTotal: overdue.length,
      noContact: noContact.slice(0, 12),
      noContactTotal: noContact.length,
      staleHot: staleHot.slice(0, 12),
      staleHotTotal: staleHot.length,
      watched: watched.slice(0, 12),
      watchedTotal: watched.length,
    };
  }, [deals, watchlist, stages]);

  // Note: watched is intentionally NOT counted in `total` — many watched
  // leads will already appear in overdue/noContact/staleHot, so adding
  // them would double-count and inflate the badge. The badge stays as a
  // signal of *issues*, not coverage.
  const total = buckets.overdueTotal + buckets.noContactTotal + buckets.staleHotTotal;

  // "f" global shortcut — only fires when not typing.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'f' || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      const typing = !!t && (
        t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' ||
        t.tagName === 'SELECT' || t.isContentEditable
      );
      if (typing) return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // CRM-only feature; tasks/funnel projects don't have leads.
  if (activeProjectType !== 'crm') return null;

  const handleOpenLead = (dealId: string) => {
    setOpen(false);
    navigate('/pipeline');
    // Tell Pipeline to focus this lead. Pipeline can listen and open the dialog.
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('legacy:focus-lead', { detail: { dealId } }));
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <SheetTrigger asChild>
            <Button
              variant="ghost" size="icon"
              className="text-muted-foreground hover:text-foreground h-8 w-8 relative"
              aria-label="Foco do dia"
            >
              <Sun className="h-4 w-4" />
              {total > 0 && (
                <span className={cn(
                  'absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center',
                  buckets.overdueTotal > 0 ? 'bg-warning text-warning-foreground' : 'bg-info text-info-foreground',
                )}>
                  {total > 99 ? '99+' : total}
                </span>
              )}
            </Button>
          </SheetTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Foco do dia <kbd className="ml-1 px-1 py-0.5 text-[9px] bg-muted rounded font-mono">F</kbd>
        </TooltipContent>
      </Tooltip>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sun className="h-4 w-4 text-warning" /> Foco do dia
          </SheetTitle>
        </SheetHeader>

        {total === 0 && buckets.watchedTotal === 0 ? (
          <div className="mt-12 flex flex-col items-center text-center gap-3">
            <div className="h-14 w-14 rounded-full bg-success/10 flex items-center justify-center">
              <Flame className="h-7 w-7 text-success" />
            </div>
            <p className="text-sm font-semibold text-foreground">Tudo em dia!</p>
            <p className="text-xs text-muted-foreground max-w-[260px]">
              Nenhum lead atrasado, sem contato recente ou esfriando. Bom trabalho.
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            {buckets.watchedTotal > 0 && (
              <BucketSection
                title="Watchlist"
                caption="Leads que você está seguindo"
                icon={Star}
                tone="watch"
                items={buckets.watched}
                total={buckets.watchedTotal}
                renderMeta={(it) => it.daysAgo < 0
                  ? 'sem update registrado'
                  : it.daysAgo === 0
                  ? 'atualizado hoje'
                  : `há ${it.daysAgo}d sem update`}
                onPick={handleOpenLead}
                onQuickLog={handleQuickLog}
                onViewAll={() => { setOpen(false); navigate('/pipeline?filter=meus-seguidos'); }}
                viewAllLabel="Ver todos os seguidos no Pipeline"
              />
            )}
            <BucketSection
              title="Atrasados"
              caption={`Sem atualização há ${OVERDUE_DAYS}+ dias`}
              icon={AlertTriangle}
              tone="warning"
              items={buckets.overdue}
              total={buckets.overdueTotal}
              renderMeta={(it) => `há ${it.daysAgo}d sem update`}
              onPick={handleOpenLead}
              onQuickLog={handleQuickLog}
              onViewAll={() => { setOpen(false); navigate('/pipeline?filter=atrasados'); }}
              viewAllLabel="Ver todos atrasados no Pipeline"
            />
            <BucketSection
              title="Sem contato há 7+ dias"
              caption="Sem registros recentes na timeline"
              icon={MessageSquare}
              tone="info"
              items={buckets.noContact}
              total={buckets.noContactTotal}
              renderMeta={(it) => it.daysAgo < 0 ? 'sem contato registrado' : `há ${it.daysAgo}d`}
              onPick={handleOpenLead}
              onQuickLog={handleQuickLog}
            />
            <BucketSection
              title="Quentes parados"
              caption={`Score ≥ ${HOT_THRESHOLD} mas sem update há ${STALE_HOT_DAYS}+ dias`}
              icon={Flame}
              tone="destructive"
              items={buckets.staleHot}
              total={buckets.staleHotTotal}
              renderMeta={(it) => `score ${it.deal.score} · há ${it.daysAgo}d`}
              onPick={handleOpenLead}
              onQuickLog={handleQuickLog}
              onViewAll={() => { setOpen(false); navigate('/pipeline?filter=quentes,atrasados'); }}
              viewAllLabel="Ver todos quentes no Pipeline"
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

interface BucketSectionProps {
  title: string;
  caption: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'warning' | 'info' | 'destructive' | 'watch';
  items: BucketItem[];
  total: number;
  renderMeta: (it: BucketItem) => string;
  onPick: (dealId: string) => void;
  onQuickLog: (deal: Deal, type: 'call' | 'whatsapp' | 'email' | 'note') => Promise<void> | void;
  /** Optional "ver todos" link — typically a navigate() into Pipeline
   *  with a URL filter macro pre-applied. */
  onViewAll?: () => void;
  viewAllLabel?: string;
}

function BucketSection({ title, caption, icon: Icon, tone, items, total, renderMeta, onPick, onQuickLog, onViewAll, viewAllLabel }: BucketSectionProps) {
  if (items.length === 0) return null;
  const toneClasses = {
    warning: 'text-warning bg-warning/10 border-warning/30',
    info: 'text-info bg-info/10 border-info/30',
    destructive: 'text-destructive bg-destructive/10 border-destructive/30',
    watch: 'text-warning bg-warning/10 border-warning/30',
  } as const;
  return (
    <section>
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={cn(
            'h-4 w-4',
            tone === 'warning' && 'text-warning',
            tone === 'info' && 'text-info',
            tone === 'destructive' && 'text-destructive',
            tone === 'watch' && 'text-warning fill-warning',
          )} />
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <Badge variant="outline" className={cn('text-[10px] h-5 px-1.5 border', toneClasses[tone])}>
            {total}
          </Badge>
        </div>
      </header>
      <p className="text-[11px] text-muted-foreground mb-2">{caption}</p>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.deal.id}>
            <div
              className="group w-full flex items-center gap-2.5 p-2 rounded-md hover:bg-muted/50 transition-colors"
              role="group"
            >
              <button
                type="button"
                className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
                onClick={() => onPick(it.deal.id)}
                aria-label={`Abrir lead ${it.deal.company}`}
              >
                <Avatar className="h-7 w-7 rounded-md shrink-0">
                  <AvatarImage src={it.deal.companyLogo || it.deal.companyInfo?.logo} />
                  <AvatarFallback
                    className="text-[9px] font-bold rounded-md"
                    style={avatarFallbackStyle(it.deal.company)}
                  >
                    {getInitials(it.deal.company)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground truncate">{it.deal.company}</p>
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
                    <Clock className="h-2.5 w-2.5 shrink-0" /> {renderMeta(it)}
                  </p>
                </div>
              </button>
              {/* Inline quick-log: call/whatsapp/email — appears on hover */}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                {it.deal.phone && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={(e) => { e.stopPropagation(); onQuickLog(it.deal, 'call'); }}
                        aria-label={`Ligar e registrar contato com ${it.deal.company}`}
                      >
                        <Phone className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">Ligar + registrar</TooltipContent>
                  </Tooltip>
                )}
                {it.deal.phone && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={(e) => { e.stopPropagation(); onQuickLog(it.deal, 'whatsapp'); }}
                        aria-label={`WhatsApp e registrar contato com ${it.deal.company}`}
                      >
                        <MessageCircle className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">WhatsApp + registrar</TooltipContent>
                  </Tooltip>
                )}
                {it.deal.email && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={(e) => { e.stopPropagation(); onQuickLog(it.deal, 'email'); }}
                        aria-label={`Email e registrar contato com ${it.deal.company}`}
                      >
                        <Mail className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">Email + registrar</TooltipContent>
                  </Tooltip>
                )}
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-foreground shrink-0 transition-colors" />
            </div>
          </li>
        ))}
        {total > items.length && (
          <li className="text-[10px] text-muted-foreground italic px-2 pt-1">
            +{total - items.length} a mais não exibidos
          </li>
        )}
      </ul>
      {onViewAll && (
        <button
          type="button"
          className="mt-1 ml-2 text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
          onClick={onViewAll}
        >
          {viewAllLabel || 'Ver todos no Pipeline'} <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </section>
  );
}
